<template>
    <v-dialog v-model="dialog" max-width="800px" persistent>
        <v-card>
            <v-card-title class="headline">
                Criar Novo Conteúdo
                <v-spacer />
                <v-btn icon @click="dialog = false">
                    <v-icon>
                        mdi-close
                    </v-icon>
                </v-btn>
            </v-card-title>

            <v-card-subtitle>
                Selecione o tipo de conteúdo e preencha o briefing
            </v-card-subtitle>

            <v-card-text v-if="!isUserInfoValid" class="text-center py-6">

                <v-row justify="center">
                    <v-col cols="12" class="mb-4">
                        <v-icon color="primary" size="48">mdi-account-alert-outline</v-icon>
                    </v-col>

                    <v-col cols="12">
                        <div class="text-h6 font-weight-bold mb-2 primary--text">
                            Informações do perfil incompletas
                        </div>

                        <div class="grey--text text--darken-1 mx-auto" style="max-width: 420px">
                            Para gerar conteúdos personalizados para suas redes sociais, precisamos que seus dados de perfil estejam completos.
                            Clique no botão abaixo para preencher as informações e volte aqui quando terminar.
                        </div>
                    </v-col>

                    <v-col cols="12" class="mt-5">
                        <v-btn color="primary" large rounded @click="$router.push('meu-perfil')">
                            <v-icon left>mdi-account-edit</v-icon>
                            Completar informações
                        </v-btn>
                    </v-col>
                </v-row>

            </v-card-text>


            <v-card-text class="black--text" v-if="isUserInfoValid">
                <v-form @submit.prevent="submit">
                    <v-row v-if="!payload.type">
                        <v-col cols="12" class="text-center">
                            <h3 class="mb-2">
                                Escolha o Tipo de Conteúdo
                            </h3>
                            <p>
                                Selecione qual assistente de redes sociais usar
                            </p>
                        </v-col>
                        <v-col v-for="t in types" :key="t.value" cols="12">
                            <v-card outlined class="cursor-pointer" @click="payload.type = t.value" :disabled="t.disabled">
                                <v-card-text class="d-flex align-center black--text">
                                    <v-icon large>
                                        {{ t.icon }}
                                    </v-icon>
                                    <div class="pl-4">
                                        <div class="font-weight-bold">
                                            {{ t.title }}
                                        </div>
                                        <div>
                                            {{ t.description }}
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Formulário quando tipo selecionado -->
                    <v-row v-if="selected" class="space-y-8">
                        <v-col class="d-flex align-center pb-8" cols="12">
                            <v-icon large>
                                {{ selected.icon }}
                            </v-icon>
                            <div class="pl-4">
                                <div class="font-weight-bold mb-1">
                                    {{ selected.title }}
                                </div>
                                <div>
                                    {{ selected.description }}
                                </div>
                            </div>
                            <v-btn class="ml-auto" text @click="payload.type = null">
                                Voltar
                            </v-btn>
                        </v-col>

                        <v-col cols="12" v-if="['admin', 'partner'].includes(currentUser.role)">
                            <v-autocomplete v-model="payload.user_id" outlined label="Cliente" clearable hide-details="auto" :items="customers" item-text="name" item-value="_id" messages="Caso não selecione um cliente, o conteúdo será gerado na sua conta">
                                <template #item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <v-col cols="12">
                            <v-combobox v-model="payload.topics" small-chips label="1. Você quer abordar quais tópicos? *" hint="Adicione os temas que deseja abordar. Pressione Enter para adicionar." outlined multiple required persistent-hint />
                        </v-col>

                        <v-col cols="12">
                            <v-textarea v-model="payload.objective" label="2. Qual seu objetivo com o conteúdo? *" rows="5" placeholder="Exemplos: atrair novos pacientes, fidelizar, educar, humanizar, desmistificar mitos, divulgar serviços/projetos" outlined persistent-placeholder hide-details />
                        </v-col>

                        <v-col cols="12">
                            <v-select v-model="payload.quantity" :items="[
                                { text: '1 conteúdo', value: 1 },
                                { text: '2 conteúdos', value: 2 },
                                { text: '3 conteúdos', value: 3 },
                                { text: '4 conteúdos', value: 4 },
                                { text: '5 conteúdos', value: 5 }
                            ]" label="3. Quantidade de conteúdos para criar *" outlined hide-details />
                        </v-col>

                        <v-col cols="12">
                            <v-select v-model="payload.priority" :items="[
                                { text: 'Educação e informação', value: 'education' },
                                { text: 'Engajamento e interação', value: 'engagement' },
                                { text: 'Autoridade e credibilidade', value: 'authority' },
                                { text: 'Humanização da marca', value: 'humanization' },
                                { text: 'Geração de leads', value: 'leads' }
                            ]" label="4. Priorização do conteúdo" placeholder="Qual aspecto você gostaria de priorizar neste conteúdo?" outlined hide-details persistent-placeholder />
                        </v-col>

                        <v-col cols="12">
                            <v-textarea v-model="payload.restrictions" label="5. Restrições" placeholder="O que você NÃO quer que seja abordado? Ex: procedimentos invasivos, polêmicas, fora da especialidade..." rows="5" hide-details persistent-placeholder outlined />
                        </v-col>

                        <v-col cols="12">
                            <v-radio-group v-model="payload.format" label="6. Formato do conteúdo" hide-details class="ma-0">
                                <template v-if="payload.type === 'youtube'">
                                    <v-radio label="Educativo (5-15 min)" value="educational" />
                                    <v-radio label="Aprofundado (15-30 min)" value="indepth" />
                                    <v-radio label="YouTube Shorts (≤60s)" value="shorts" />
                                    <v-radio label="Entrevista/bate-papo" value="interview" />
                                    <v-radio label="Estudo de caso" value="casestudy" />
                                    <v-radio label="Webinar educativo" value="webinar" />
                                </template>

                                <template v-if="payload.type === 'static_post'">
                                    <v-radio label="Post único" value="single" />
                                    <v-radio label="Carrossel (2-10 slides)" value="carousel" />
                                    <v-radio label="Infográfico educativo" value="infographic" />
                                    <v-radio label="Citação/frase motivacional" value="quote" />
                                    <v-radio label="Lista de dicas" value="tips" />
                                    <v-radio label="Mitos e verdades" value="myths" />
                                </template>

                                <template v-if="payload.type === 'reels'">
                                    <v-radio label="Reels curtos (≤30s)" value="short" />
                                    <v-radio label="Explicativos (60-120s)" value="explanatory" />
                                    <v-radio label="Storytelling (caso/relato)" value="storytelling" />
                                    <v-radio label="FAQ (perguntas rápidas)" value="faq" />
                                    <v-radio label="Bastidores/rotina" value="behindscenes" />
                                    <v-radio label="Demonstração/tutoriais" value="demo" />
                                </template>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions v-if="payload.type" class="justify-end">
                <v-btn text @click="dialog = false">
                    Voltar
                </v-btn>
                <v-btn color="primary" large :disabled="!isFormValid" :loading="saving" @click="submit">
                    Gerar Conteúdo
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            saving: false,
            types: [
                {
                    title: 'Criação de Videos para Reels',
                    description: 'Roteiros para vídeos curtos (Instagram Reels, TikTok)',
                    value: 'reels',
                    icon: 'mdi-play-circle-outline',
                    disabled: false
                },
                {
                    title: 'Criação de Vídeos para YouTube',
                    description: 'Roteiros para vídeos longos e shorts',
                    value: 'youtube',
                    icon: 'mdi-youtube',
                    disabled: false
                },
                {
                    title: 'Criação de Posts Estáticos',
                    description: 'Posts para Instagram, Facebook, LinkedIn',
                    value: 'static_post',
                    icon: 'mdi-post',
                    disabled: false
                }
            ],
            payload: {
                type: null,
                objective: '',
                topics: null,
                quantity: 1,
                priority: null,
                restrictions: '',
                format: null,
                specialty: '',
                target_audience: '',
                professional_bio: '',
                city: '',
                state: '',
                telemedicine: null,
                care_differentials: '',
                additional_information: ''
            }
        }
    },
    async mounted() {
        await this.$store.dispatch('loadCurrentUser');
    },
    computed: {
        dialog: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            }
        },
        currentUser() {
            return this.$store.state.currentUser
        },
        selected() {
            return this.types.find(t => t.value === this.payload.type)
        },
        isFormValid() {
            return this.payload.type &&
                this.payload.objective &&
                this.payload.topics &&
                this.payload.quantity
        },
        isUserInfoValid() {
            if (this.currentUser.name && this.currentUser.specialty && this.currentUser.target_audience && this.currentUser.professional_bio) return true;
            return false;
        },
        customers() {
            return this.$store.state.users.data.filter(u => u.role === 'user')
        }
    },
    methods: {
        reset() {
            this.payload = {
                type: null,
                objective: '',
                topics: null,
                quantity: 1,
                priority: null,
                restrictions: '',
                format: null,
                specialty: '',
                target_audience: '',
                professional_bio: '',
                city: '',
                state: '',
                telemedicine: null,
                care_differentials: '',
                additional_information: ''
            }
        },
        async submit() {
            this.saving = true;
            this.payload.specialty = this.currentUser.specialty;
            this.payload.target_audience = this.currentUser.target_audience;
            this.payload.professional_bio = this.currentUser.professional_bio;
            this.payload.city = this.currentUser.city;
            this.payload.state = this.currentUser.state;
            this.payload.telemedicine = this.currentUser.telemedicine;
            this.payload.care_differentials = this.currentUser.care_differentials;
            this.payload.additional_information = this.currentUser.additional_information;

            const response = await this.$api.createSocialPlan(this.payload)

            if (response.error) {
                this.saving = false
                return
            }

            setTimeout(() => {
                this.saving = false
                this.dialog = false

                this.$toast('success', 'Plano criado com sucesso, em breve os posts estarão disponíveis!')

                this.$emit('submit', response.message)

                this.reset()
            }, 1000)
        },
    }
}
</script>
